// angular
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// libs
import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';

// C4 libs
import { BrokerService } from '@c4/core';

// common
import { ItemsService } from './../../../common/services/items.service';
import { Event } from './../../../common/interfaces/event.interface';
import { ProgrammingUtilsService as Util } from '../../../common/services/utils.service';
import { SharedProgrammingService, CommandData, CommonProgrammingContext } from '../../../common/services/shared-programming.service';
import { Command } from '../../../common/params/interfaces/command.interface';
import { Parameter, ValueType } from '../../../common/params/interfaces/parameter.interface';
import { DeviceEvent } from '../../../common/interfaces/event.interface';

// module
import { Light, LightSceneRef, LightSceneDetail } from './interfaces/light.interface';
import { Room } from './interfaces/room.interface';
import { Button } from './interfaces/button.interface';
import { LightsProgrammingContext } from './lights.service';
import { Item, Device, Location } from '../../../common/interfaces/item.interface';
import { ItemsState } from '../../../common/services/items.service';

/**
 * List of commands supported by
 * the lights quickstart
 *
 * @type {Array<string>}
 */
const COMMAND_WHITELIST = ['ON', 'OFF', 'TOGGLE', 'SET_LEVEL'];

/**
 * List of events supported by
 * the lights quickstart
 *
 * @type {Array<number>}
 */
export const EVENT_WHITELIST = [
  1,    // push top
  2,    // release top
  3,    // push bottom
  4,    // release bottom
  // 5,    // change level
  // 5000, // change level
  5001, // single tap top
  5002, // double tap top
  5003, // triple tap top
  5004, // double tap top
  5005, // triple tap top
  5006, // release bottom
  5007, // press bottom
  5008, // single tap bottom
  5009, // double tap bottom
  5010, // triple tap bottom
  // 5011, // release toggle
  // 5012, // press toggle
  // 5013, // single tap toggle
  // 5014, // double tap toggle
  // 5015, // triple tap toggle
  5101, // single tap bottom
  5102, // double tap bottom
  5103, // triple tap bottom
];

/**
 * Pulled from Broker
 * @type {Array<string>}
 */
const LIGHT_PROXIES = [
  'light_v2',
  'light',
  'fan',
  'outlet_wireless',
  'outlet_light',
  'outlet_switch_module',
  'outlet_dimmer_module',
  'din_rail_2_panel',
  'din_rail_5_panel',
  'din_rail_8_channel_dimmer_module',
  'din_rail_8_channel_0-10V_module',
  'din_rail_8_port_ethernet_switch ',
  'din_rail_8_channel_relay_module'
];

export interface LightsProgrammingContext {
  lights: Light[];
  trigger?: Trigger;
  action?: Action;

  roomScenes?: LightSceneRef[];
  scene?: LightSceneDetail;
  initialized: boolean;
}

interface Trigger {
  light?: Light;
  events?: DeviceEvent[];
  button?: Button;
  event?: DeviceEvent;
}

interface Action {
  light?: Light;
  commands?: Command[];
  command?: CommandData;
}

const INITIAL_STATE: LightsProgrammingContext = {
  lights: [],
  trigger: {},
  action: {},
  initialized: false
}

const STORE_NAME = ['PROGRAMMING', 'QUICKSTARTS', 'LIGHTS'].join(':');

const ACTIONS = {
  SET_LIGHTS: `${STORE_NAME}:SET_LIGHTS`,
  SET_TRIGGER: `${STORE_NAME}:SET_TRIGGER`,
  SET_EVENTS: `${STORE_NAME}:SET_EVENTS`,
  SET_BUTTON: `${STORE_NAME}:SET_BUTTON`,
  SET_EVENT: `${STORE_NAME}:SET_EVENT`,
  SET_ACTION: `${STORE_NAME}:SET_ACTION`,
  SET_COMMANDS: `${STORE_NAME}:SET_COMMANDS`,
  SET_COMMAND: `${STORE_NAME}:SET_COMMAND`,
  SET_COMMAND_VALUES: `${STORE_NAME}:SET_COMMAND_VALUES`,

  SET_ROOM_SCENES: `${STORE_NAME}:SET_ROOM_SCENES`,
  SET_SCENE: `${STORE_NAME}:SET_SCENE`,
  SET_INITIALIZED: `${STORE_NAME}:SET_INITIALIZED`,
}

const ACTION_STRINGS = Object.keys(ACTIONS).map(key => ACTIONS[key]);

export function lightsProgrammingReducer(state: LightsProgrammingContext = INITIAL_STATE, { type, payload }): LightsProgrammingContext {

  // if (ACTION_STRINGS.indexOf(type) > -1) {
  //   console.info('%s called with %O', type, payload);
  // }

  switch (type) {
    case ACTIONS.SET_ROOM_SCENES:
      return Object.assign({}, state, { roomScenes: payload });
    case ACTIONS.SET_SCENE:
      return Object.assign({}, state, { scene: payload });
    case ACTIONS.SET_INITIALIZED:
      return Object.assign({}, state, { initialized: payload });

    case ACTIONS.SET_LIGHTS:
      return Object.assign({}, state, { lights: payload });

    case ACTIONS.SET_TRIGGER:
      let t1 = Object.assign({}, state.trigger, { light: payload });
      return Object.assign({}, state, { trigger: t1 });

    case ACTIONS.SET_EVENTS:
      let t2 = Object.assign({}, state.trigger, { events: payload });
      return Object.assign({}, state, { trigger: t2 });

    case ACTIONS.SET_BUTTON:
      let t3 = Object.assign({}, state.trigger, { button: payload });
      return Object.assign({}, state, { trigger: t3 });

    case ACTIONS.SET_EVENT:
      let t4 = Object.assign({}, state.trigger, { event: payload });
      return Object.assign({}, state, { trigger: t4 });

    case ACTIONS.SET_ACTION:
      let a1 = Object.assign({}, state.action, { light: payload });
      return Object.assign({}, state, { action: a1 });

    case ACTIONS.SET_COMMANDS:
      // console.log('lights: setting commands', payload);
      let a2 = Object.assign({}, state.action, { commands: payload });
      return Object.assign({}, state, { action: a2 });

    case ACTIONS.SET_COMMAND:
      let a3 = Object.assign({}, state.action, { command: payload });
      return Object.assign({}, state, { action: a3 });

    case ACTIONS.SET_COMMAND:
      let s1 = Object.assign({}, state.action, { command: payload });
      return Object.assign({}, state, { scenes: s1 });

    case ACTIONS.SET_COMMAND_VALUES:
      let commands: Command[] = [...state.action.commands];
      let commandIx = commands.findIndex(c => {
        return c.command === payload.command.command &&
          c.id === payload.command.id
      });

      commands[commandIx].params = [...payload.params];

      return Object.assign({}, state, { action: Object.assign({}, state.action, { commands: commands }) });

    default:
      return state;
  }
}

@Injectable()
export class LightsService {

  private _lights: Observable<Light[]>;

  /**
   * Lights with whitelisted events
   */
  public triggerLights: Observable<Light[]>;

  /**
   * Lights with whitelisted commands
   */
  public actionLights: Observable<Light[]>;

  // TOOD re-evalute this approach
  // NOTE when saving a lighting scene command it is not safe to assume that the trigger is
  // always a light, in fact in many cases it will not be.  The shared programming service
  // must own the selected trigger because some triggers require pre-processing before the
  // codeitem (command) can be saved (i.e., schedules), probably need to examine whether or
  // not the lights service really needs to own the selected trigger here.
  private _trigger: Observable<Trigger>;
  private _triggerDevice: Observable<any>;
  private _triggerEvent: Observable<DeviceEvent>;

  private _action: Observable<Action>;
  private _actionCommand: Observable<CommandData>;

  private _sharedTriggerEvent: Observable<Event>;

  /**
   * Convenience observables that group
   * lights by the room they belong to,
   * and appends the appropriate Room
   * parameters
   */
  public lightsByRoom: Observable<Room[]>;
  public triggerLightsByRoom: Observable<Room[]>;
  public actionLightsByRoom: Observable<Room[]>;

  public roomScenes: Observable<LightSceneRef[]>;
  public scene: Observable<LightSceneDetail>;
  public initialized: Observable<boolean>;

  constructor(
    private _broker: BrokerService,
    private _store: Store<{
      lightsProgramming: LightsProgrammingContext,
      sharedProgramming: CommonProgrammingContext,
      programmingItems: ItemsState
    }>,
    private _shared: SharedProgrammingService,
    private _items: ItemsService,
    private _router: Router,
    private _utils: Util
  ) {

    this.roomScenes = this._store.select(s => s.lightsProgramming.roomScenes);
    this.scene = this._store.select(s => s.lightsProgramming.scene);
    this.initialized = this._store.select(s => s.lightsProgramming.initialized);

    // this._lights = this._store.select(s => s.lightsProgramming.lights);
    this._lights = <Observable<Light[]>>this._items.itemsList.map(items => {
      return items.filter((item: Device) => {
        return (LIGHT_PROXIES.indexOf(item.proxy) > -1 && item.type === 7)
      });
    });

    this.lightsByRoom = <Observable<Room[]>>this._byRoom(this._lights);

    this.triggerLights = this._lights.map(lights => {
      return lights.filter(light => {
        // HACK silly me, assuming that event ids would be represented consistently (id not eventId)
        return light.events && light.events.some((e: any) => EVENT_WHITELIST.indexOf(e.id) > -1);
      })
    });
    this.triggerLightsByRoom = this._byRoom(this.triggerLights);

    this.actionLights = this._lights.map(lights => lights.filter((light: Light) => {
      // console.log('lights: filtering light for actions', light);
      return light.commands && light.commands.command.some(c => COMMAND_WHITELIST.indexOf(c.name) > -1);
    }));
    this.actionLightsByRoom = this._byRoom(this.actionLights);

    this._trigger = this._store.select(s => s.lightsProgramming.trigger);
    this._triggerEvent = <Observable<DeviceEvent>>this._trigger
      .map(t => (!!t) ? t.event : undefined)
      .distinctUntilChanged(Util.distinct);
    this._triggerDevice = this._store.select(s => s.lightsProgramming.trigger.light)
      .filter(l => !!l)
      .distinctUntilChanged(Util.distinct);

    this._action = this._store.select(s => s.lightsProgramming.action);
    this._actionCommand = this._action.filter(a => !!a).map((a: any) => a.command);

    this._sharedTriggerEvent = this._store.select(s => s.sharedProgramming.trigger.event);

    this._monitorAction();
    this._monitorTrigger();
  }

  saveAction = (deviceId: number, eventId: number): Promise<any> => {
    let command = Util.snapshot(this._store.select(s => s.lightsProgramming.action.command));

    return this._broker.call({
      path: ['/api/v1/items', deviceId, 'events', eventId, 'commands'].join('/'),
      method: 'POST',
      data: command
    })
  }

  saveTrigger = (): Promise<any> => {
    return new Promise((resolve, reject) => resolve());
  }

  setTrigger(event: Event) {
    this._store.dispatch({ type: ACTIONS.SET_EVENT, payload: event });
  }

  setTriggerLight(l: Light | number): void {
    // console.log('lights.settrigger: ', l);
    if (typeof l == 'number') {
      this._getLightById(l)
        .filter(l => !!l)
        .take(1)
        .subscribe(l => this._store.dispatch({
          type: ACTIONS.SET_TRIGGER,
          payload: l
        }))
    } else {
      this._store.dispatch({
        type: ACTIONS.SET_TRIGGER,
        payload: l
      });
    }
  }

  setTriggerButton(button: Button): void {
    this._store.dispatch({ type: ACTIONS.SET_BUTTON, payload: button });
  }

  setTriggerEvent(event: DeviceEvent): void {
    this._router.navigate(['../../device', event.deviceId, 'event', event.eventId, 'add'], { relativeTo: this._shared.moduleRoot });
  }

  setActionLight(l: Light | number): void {
    // console.log('lights.setaction', l);
    if (typeof l == 'number') {
      this._getLightById(l)
        .filter(l => !!l)
        .take(1)
        .subscribe(l => {
          // console.log('lights.setaction: found light for id', l);
          this._store.dispatch({
            type: ACTIONS.SET_ACTION,
            payload: l
          })
        })
    } else {
      this._store.dispatch({
        type: ACTIONS.SET_ACTION,
        payload: l
      });
    }
  }

  setActionCommand(command: Command, params: Parameter[] = []): Promise<any> {

    // Hidden params will have a value set, but
    // no _value. Set it.
    params = params.filter(p => !!p).map(p => {
      p._value = p._value || p.value;
      return p;
    });

    let light: Light = <Light>Util.snapshot(this._store.select(s => s.lightsProgramming.action.light));

    let data: CommandData = {
      codeitemId: 0,
      deviceId: light.id,
      command: command.command,
      beforeCodeItemId: 0,
      parentCodeItemId: 0,
      creator: '',
      type: 1
    };

    if (params.length > 0) {
      data.params = [];
      params.forEach((param, i) => {
        data.params.push({
          name: param.name,
          value: param._value
        })
      });
    }

    this._store.dispatch({ type: ACTIONS.SET_COMMAND, payload: command });

    return this._shared.saveAction(data);
  }

  updateParam = (evt: ValueType, param: Parameter, command: Command) => {
    let newParam: Parameter = Object.assign({}, param, {
      _value: evt.value,
      _display: evt.display
    });

    let params = [...command.params];

    let pix = params.findIndex(p => p.name === newParam.name && p.type === newParam.type);

    params[pix] = newParam;

    this.updateParams(params, command);
  }

  updateParams = (params: Parameter[], command: Command) => {
    this._store.dispatch({
      type: ACTIONS.SET_COMMAND_VALUES,
      payload: {
        params: params,
        command: command
      }
    })
  }

  public getScene(id: number) {
    this._broker.call({
      path: '/api/v1/agents/advanced_lighting/' + id
    }).then(res => {
      this._store.dispatch({ type: ACTIONS.SET_SCENE, payload: res[0] });
    }, err => {
      console.error('lights.service.ts: error: %0', err);
    });
  }

  public setSceneCommand(name: string) {
    let agent = this._findSceneAgent()
    if (!!agent) {
      this._saveSceneCommand(agent.id, name);
    }
  }

  private _findSceneAgent(): Item {
    let agent = undefined;

    this._store.select(s => s.programmingItems.itemsList)
      .map(items => {
        return items.filter((item: Device) => {
          return item.type === 9 && item.control === 'control4_agent_adv_lighting';
        })
      })
      .take(1)
      .subscribe(agents => {
        if (agents && agents.length === 1) {
          agent = agents[0];
        }
      });

    if (!agent) {
      console.error('lights: unable to find advanced lighting scene agent in project');
    }

    return agent;
  }

  private _saveSceneCommand(agentId: number, name: string) {
    let scene = Util.snapshot<LightSceneDetail>(this.scene);
    // let trigger = Util.snapshot(this._trigger);
    let event = Util.snapshot<Event>(this._sharedTriggerEvent);

    let codeitem: any = {
      codeitemId: 0,
      deviceId: agentId,
      command: name,
      beforeCodeItemId: 0,
      parentCodeItemId: 0,
      creator: '',
      type: 1,
      params: [{
        name: 'SCENE_ID',
        type: 'INT',
        value: scene.scene_id
      }]
    };

    codeitem.creatorState = this._utils.buildCreatorState(event, codeitem);

    // console.log('lights.service: saving scene codeitem', codeitem);

    this._store.dispatch({ type: ACTIONS.SET_COMMAND, payload: name });
    this._shared.saveAction(codeitem);
  }

  /**
   * Monitors the action in the
   * store, and sets commands as
   * it changes
   */
  private _monitorAction = () => {
    // console.log('lights: monitoring action ');
    this._action
      .filter(a => !!a)
      .map((a: any) => {
        // console.log('lights: light changed', a);
        return (!!a.light && !!a.light.URIs && !!a.light.URIs.commands) ?
          a.light.URIs.commands :
          undefined;
      })
      .distinctUntilChanged(Util.distinct)
      .subscribe(
      uri => {
        // console.log('lights: light uri is', uri);
        if (uri === undefined) {
          this._store.dispatch({ type: ACTIONS.SET_COMMANDS, payload: undefined });
        } else {
          // console.log('lights: getting commands for light', uri);
          this._broker.call({
            path: uri
          }).then(
            (commands: Command[]) => {
              // console.log('lights: whitelisting light commands', commands);
              let whiteListed = commands.filter(c => COMMAND_WHITELIST.indexOf(c.command) > -1);
              this._store.dispatch({ type: ACTIONS.SET_COMMANDS, payload: whiteListed });
            },
            error => console.error('lights.service.ts: error: %O', error)
            );
        }
      },
      error => console.error('lights.service.ts: error: %O', error)
      );
  }

  private _monitorTrigger = () => {
    // console.log('lights: monitoring trigger');
    this._triggerDevice
      .filter(t => !!t)
      .distinctUntilChanged(Util.distinct)
      .subscribe(light => {
        // console.log('lights: getting events for trigger device', light);
        this._broker.call({
          path: ['/api/v1/items', light.id, 'events'].join('/'),
          queryString: {
            allevents: true
          }
        }).then(res => {
          this._store.dispatch({ type: ACTIONS.SET_EVENTS, payload: res });
        }, err => {
          console.error('lights: error getting events for trigger light', err);
        });
      });
  }

  private _getLightById = (id: number): Observable<Light> => {
    return this._lights.map(lights => lights.find(l => l.id === id));
  }

  private _byRoom = (lightsObservable: Observable<Light[]>): Observable<Room[]> => {
    return lightsObservable.map(lights => lights.reduce((rooms: Room[], light) => {
      let room = rooms.find(r => r.roomId === light.roomId);

      if (room === undefined) {
        room = {
          roomName: light.roomName,
          roomId: light.roomId,
          devices: []
        }

        rooms.push(room);
      }

      room.devices.push(light);

      return rooms;
    }, []));
  }
}



// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/quickstarts/quickstart-lights/services/lights.service.ts